import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import carImg from "../../img/objectexplained.jpg";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Objects() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🚗 JavaScript Objects</h1>

      {/* 💡 Intro Section */}
      <div
        className="Key_point_container"
        style={{
          backgroundColor: "#d1fae5",
        }}
      >
        <p>
          An <b>Object</b> is a variable that can hold many variables.
        </p>
        <p>
          Objects are collections of <b>key–value pairs</b>, where each key
          (known as a <b>property name</b>) has a corresponding value.
        </p>
        <p>
          Objects can describe anything — like <b>houses</b>, <b>cars</b>,{" "}
          <b>people</b>, or <b>animals</b>.
        </p>
      </div>

      {/* 🧱 Car Object Explanation */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "1rem",
          alignItems: "start",
          backgroundColor: "#f9fafb",
          padding: "1rem",
          borderRadius: "12px",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      >
        {/* Car Object */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              backgroundColor: "#e5e7eb",
              margin: 0,
              padding: "10px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              fontSize: "1.1rem",
            }}
          >
            Car Object
          </h3>
          <div style={{ padding: "10px", textAlign: "center" }}>
            <img
              src={carImg}
              alt="Car"
              style={{ width: "100%", borderRadius: "8px" }}
            />
          </div>
        </div>

        {/* Car Properties */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              backgroundColor: "#e5e7eb",
              margin: 0,
              padding: "10px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              fontSize: "1.1rem",
            }}
          >
            Car Properties
          </h3>
          <ul
            style={{
              padding: "10px",
              lineHeight: "1.7",
              listStyleType: "none",
            }}
          >
            <li>car.name = Honda</li>
            <li>car.model = 500</li>
            <li>car.weight = 850kg</li>
            <li>car.color = white</li>
          </ul>
        </div>

        {/* Car Methods */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              backgroundColor: "#e5e7eb",
              margin: 0,
              padding: "10px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              fontSize: "1.1rem",
            }}
          >
            Car Methods
          </h3>
          <ul
            style={{
              padding: "10px",
              lineHeight: "1.7",
              listStyleType: "none",
            }}
          >
            <li>car.start()</li>
            <li>car.drive()</li>
            <li>car.brake()</li>
            <li>car.stop()</li>
          </ul>
        </div>
      </div>

      {/* ➕ Basic Object Declaration Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🧠 JavaScript Objects</h2>
        <p>
          This code assigns many values (<b>Honda</b>, <b>500</b>, <b>white</b>)
          to an object named <b>car</b>:
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const car = {type: "Honda", model: "500", color: "white"};
console.log(car.type);
console.log(car.model);
console.log(car.color);`}
          />
          <TryItButton
            code={`const car = {type: "Honda", model: "500", color: "white"};
console.log(car.type);
console.log(car.model);
console.log(car.color);`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          <pre>
            Honda{"\n"}
            500{"\n"}
            white
          </pre>
        </div>
      </div>

      {/* 🧩 Additional Explanation */}
      <div
        style={{
          marginTop: "1.5rem",
          fontSize: "1.1rem",
          lineHeight: "1.7",
        }}
      >
        <p>
          Different cars have the same <b>properties</b>, but the{" "}
          <b>property values</b> can differ from car to car.
        </p>
        <p>
          Different cars also share the same <b>methods</b>, but they can be
          executed at <b>different times</b>.
        </p>
      </div>

      {/* Key Points Table */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1.1rem",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <thead style={{ backgroundColor: "#e2e8f0" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" }}>Concept</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Description
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>Object Literal</td>
              <td style={{ padding: "10px" }}>
                Create objects using curly braces <code>&#123;&#125;</code>
              </td>
              <td style={{ padding: "10px" }}>const obj = &#123;&#125;;</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>Access Properties</td>
              <td style={{ padding: "10px" }}>Use dot or bracket notation</td>
              <td style={{ padding: "10px" }}>obj.name or obj["name"]</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>Add/Modify Properties</td>
              <td style={{ padding: "10px" }}>Assign values dynamically</td>
              <td style={{ padding: "10px" }}>obj.age = 25;</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>Methods</td>
              <td style={{ padding: "10px" }}>Functions inside objects</td>
              <td style={{ padding: "10px" }}>
                obj.greet = function()&#123;&#125;
              </td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>this Keyword</td>
              <td style={{ padding: "10px" }}>Refers to the current object</td>
              <td style={{ padding: "10px" }}>this.name</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example 1: Object Creation */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Object Creation</h2>
        <p>You can create an object using curly braces and key-value pairs.</p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Edu-Tech Master Academy",
  age: 25,
  city: "Mumbai"
};

console.log(person);`}
          />
          <TryItButton
            code={`const person = {
  name: "Edu-Tech Master Academy",
  age: 25,
  city: "Mumbai"
};

console.log(person);`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          <pre>{`{ name: "Edu-Tech Master Academy", age: 25, city: "Mumbai" }`}</pre>
        </div>
      </div>

      {/* Example 2: Accessing Object Properties */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Accessing Object Properties</h2>
        <p>
          You can access object properties using <b>dot notation</b> or{" "}
          <b>bracket notation</b>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const car = {
  name: "Edu-Tech Master Academy",
  model: "Model X"
};

console.log(car.name);
console.log(car["model"]);`}
          />
          <TryItButton
            code={`const car = {
  name: "Edu-Tech Master Academy",
  model: "Model X"
};

console.log(car.name);
console.log(car["model"]);`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          <pre>
            Edu-Tech Master Academy
            <br />
            Model X
          </pre>
        </div>
      </div>

      {/* Example 3: Adding and Updating Properties */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Adding and Updating Properties</h2>
        <p>
          You can dynamically add or change object properties after creation.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const student = { name: "Edu-Tech Master Academy", age: 20 };
student.course = "JavaScript";
student.age = 21;

console.log(student);`}
          />
          <TryItButton
            code={`const student = { name: "Edu-Tech Master Academy", age: 20 };
student.course = "JavaScript";
student.age = 21;

console.log(student);`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          <pre>{`{ name: "Edu-Tech Master Academy", age: 21, course: "JavaScript" }`}</pre>
        </div>
      </div>

      {/* Example 4: Object Methods */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Object Methods</h2>
        <p>
          You can define functions inside objects, called <b>methods</b>, to
          perform actions.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Edu-Tech Master Academy",
  greet: function() {
    console.log("Hello, " + this.name);
  }
};

person.greet();`}
          />
          <TryItButton
            code={`const person = {
  name: "Edu-Tech Master Academy",
  greet: function() {
    console.log("Hello, " + this.name);
  }
};

person.greet();`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          <pre>Hello, Edu-Tech Master Academy</pre>
        </div>
      </div>

      {/* Example 5: Nested Objects */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Nested Objects</h2>
        <p>
          Objects can contain other objects as values for more structured data.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const user = {
  name: "Edu-Tech Master Academy",
  contact: {
    email: "info@edutechacademy.com",
    phone: "1234567890"
  }
};

console.log(user.contact.email);`}
          />
          <TryItButton
            code={`const user = {
  name: "Edu-Tech Master Academy",
  contact: {
    email: "info@edutechacademy.com",
    phone: "1234567890"
  }
};

console.log(user.contact.email);`}
          />
        </div>

        <h3 style={{ marginTop: "1rem" }}>Output:</h3>
        <div
          style={{
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          <pre>info@edutechacademy.com</pre>
        </div>
      </div>

      {/* Next Steps */}

      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Object Methods</b> like <code>Object.keys()</code>,{" "}
            <code>Object.values()</code>, and <code>Object.entries()</code>.
          </li>
          <li>
            Understand <b>Object Destructuring</b> and <b>Spread Operator</b>.
          </li>
          <li>
            Explore <b>Classes</b> and <b>Prototypes</b> for object-oriented
            JavaScript.
          </li>
        </ul>
      </div>
    </div>
  );
}
